import { Typography, useTheme } from "@mui/material";
import { FC } from "react";
import CompanyTableRow from "./CompanyTableRow";
import { useTranslation } from "react-i18next";
import { CompanyType } from "../../types/companyManagementTypes";
import {
  CompanyManagementTableStyles,
  CompanyTableTitle,
  EmptyRow,
  InvalidBillingAdressRow,
} from "./CompanyManagement.styles";
import { EditIcon } from "../../assets/icons/Icons";
import { usePermissionChecker } from "../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../types/permissionType";
import { GeneralButton } from "../../common/ui/GeneralButton";

interface CompanyInfoTableProps {
  title: string;
  companyTableInfo: CompanyType;
  displayCanNotBeProcessed: boolean;
  invoiceEmail?: string;
  isSubcompany?: boolean;
  onEdit?: (company: CompanyType) => void;
}

const CompanyInfoTable: FC<CompanyInfoTableProps> = ({
  title,
  companyTableInfo,
  displayCanNotBeProcessed,
  invoiceEmail,
  isSubcompany = false,
  onEdit
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { name, address } = companyTableInfo;
  const {
    street,
    extraAddressLine,
    postalCode,
    town,
    number,
    country,
    canBeProcessed,
  } = address;

  const { userHasPermission } = usePermissionChecker();
  const canEditSubCompany =
    isSubcompany &&
    onEdit &&
    (userHasPermission(PermissionEntityType.SubCompanies, "Edit") ||
      userHasPermission(PermissionEntityType.SubCompanies, "Edit", companyTableInfo.id));
  return (
    <CompanyManagementTableStyles>
      <CompanyTableTitle>
        {title}
        {canEditSubCompany && (
          <GeneralButton
            appVariant="icon"
            onClick={() => onEdit(companyTableInfo)}
            sx={{ ml: 1 }}
          >
            <EditIcon color={theme.palette.primary.main} />
          </GeneralButton>
        )}
      </CompanyTableTitle>
      <CompanyTableRow
        title={t("companyManagementPage.companyInfoTable.company")}
        info={name}
      />
      <CompanyTableRow
        title={t("companyManagementPage.companyInfoTable.streetNo")}
        info={street + " " + number}
      />
      <CompanyTableRow
        title={t("companyManagementPage.companyInfoTable.extraAddressLine")}
        info={extraAddressLine}
      />
      <CompanyTableRow
        title={t("companyManagementPage.companyInfoTable.postalCode")}
        info={postalCode}
      />
      <CompanyTableRow
        title={t("companyManagementPage.companyInfoTable.city")}
        info={town}
      />
      <CompanyTableRow
        title={t("companyManagementPage.companyInfoTable.country")}
        info={country}
      />
      {invoiceEmail !== undefined ?
        <CompanyTableRow
          title={t("companyManagementPage.companyInfoTable.invoiceEmail")}
          info={invoiceEmail !== ""
            ? invoiceEmail
            : "-"
          }
          isEmail
        />
        : null
      }
      {(canBeProcessed && displayCanNotBeProcessed) ||
        !displayCanNotBeProcessed ? (
        <EmptyRow />
      ) : (
        <InvalidBillingAdressRow>
          <Typography>
            {t(
              "companyManagementPage.companyInfoTable.isBillingAddressProcessed"
            )}
          </Typography>
          <Typography>
            {t("companyManagementPage.companyInfoTable.contactSupport")}{" "}
            <a href="mailto:support@bonisoft.de">support@bonisoft.de</a> .
          </Typography>
        </InvalidBillingAdressRow>
      )}
    </CompanyManagementTableStyles>
  );
};

export default CompanyInfoTable;
